/* This file is auto-generated */

import type { ContractPromise } from '@polkadot/api-contract';
import type { ApiPromise } from '@polkadot/api';
import type { GasLimit, Result } from '@727-ventures/typechain-types';
import type { QueryReturnType } from '@727-ventures/typechain-types';
import { queryOkJSON, handleReturnType } from '@727-ventures/typechain-types';
import type * as ArgumentTypes from '../types-arguments/capsule';
import type * as ReturnTypes from '../types-returns/capsule';
import type BN from 'bn.js';
import type { ReturnNumber } from '@727-ventures/typechain-types';
import { getTypeDescription } from './../shared/utils';
import DATA_TYPE_DESCRIPTIONS from '../data/capsule.json';

export default class Methods {
	readonly __nativeContract: ContractPromise;
	readonly __apiPromise: ApiPromise;
	readonly __callerAddress: string;

	constructor(nativeContract: ContractPromise, nativeApi: ApiPromise, callerAddress: string) {
		this.__nativeContract = nativeContract;
		this.__callerAddress = callerAddress;
		this.__apiPromise = nativeApi;
	}

	/**
	 * mint
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @param { (string | number | BN) } amount,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	mint(
		account: ArgumentTypes.AccountId,
		amount: string | number | BN,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'mint',
			[account, amount],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * burn
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @param { (string | number | BN) } amount,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	burn(
		account: ArgumentTypes.AccountId,
		amount: string | number | BN,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'burn',
			[account, amount],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * holders
	 *
	 * @returns { Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError> }
	 */
	holders(
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'holders',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(16, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * transfer
	 *
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	transfer(
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'transfer',
			[to, value, data],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * transferFrom
	 *
	 * @param { ArgumentTypes.AccountId } from,
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	transferFrom(
		from: ArgumentTypes.AccountId,
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'transferFrom',
			[from, to, value, data],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * renounceOwnership
	 *
	 * @returns { Result<Result<null, ReturnTypes.OwnableError>, ReturnTypes.LangError> }
	 */
	renounceOwnership(
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.OwnableError>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'ownable::renounceOwnership',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(18, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * owner
	 *
	 * @returns { Result<ReturnTypes.AccountId | null, ReturnTypes.LangError> }
	 */
	owner(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<ReturnTypes.AccountId | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'ownable::owner',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(21, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * transferOwnership
	 *
	 * @param { ArgumentTypes.AccountId | null } newOwner,
	 * @returns { Result<Result<null, ReturnTypes.OwnableError>, ReturnTypes.LangError> }
	 */
	transferOwnership(
		newOwner: ArgumentTypes.AccountId | null,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.OwnableError>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'ownable::transferOwnership',
			[newOwner],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(18, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * increaseAllowance
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } deltaValue,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	increaseAllowance(
		spender: ArgumentTypes.AccountId,
		deltaValue: string | number | BN,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::increaseAllowance',
			[spender, deltaValue],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * approve
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } value,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	approve(
		spender: ArgumentTypes.AccountId,
		value: string | number | BN,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::approve',
			[spender, value],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * balanceOf
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	balanceOf(
		owner: ArgumentTypes.AccountId,
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::balanceOf',
			[owner],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * allowance
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @param { ArgumentTypes.AccountId } spender,
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	allowance(
		owner: ArgumentTypes.AccountId,
		spender: ArgumentTypes.AccountId,
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::allowance',
			[owner, spender],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * allowances
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @returns { Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError> }
	 */
	allowances(
		owner: ArgumentTypes.AccountId,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Array<[ReturnTypes.AccountId, ReturnNumber]>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::allowances',
			[owner],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(16, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * psp22::transfer
	 *
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	'psp22::transfer'(
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::transfer',
			[to, value, data],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * psp22::transferFrom
	 *
	 * @param { ArgumentTypes.AccountId } from,
	 * @param { ArgumentTypes.AccountId } to,
	 * @param { (string | number | BN) } value,
	 * @param { Array<(number | string | BN)> } data,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	'psp22::transferFrom'(
		from: ArgumentTypes.AccountId,
		to: ArgumentTypes.AccountId,
		value: string | number | BN,
		data: Array<number | string | BN>,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::transferFrom',
			[from, to, value, data],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * decreaseAllowance
	 *
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } deltaValue,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	decreaseAllowance(
		spender: ArgumentTypes.AccountId,
		deltaValue: string | number | BN,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::decreaseAllowance',
			[spender, deltaValue],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * totalSupply
	 *
	 * @returns { Result<ReturnNumber, ReturnTypes.LangError> }
	 */
	totalSupply(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<ReturnNumber, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22::totalSupply',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(23, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * domainSeparator
	 *
	 * @returns { Result<Array<number>, ReturnTypes.LangError> }
	 */
	domainSeparator(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<Array<number>, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Permit::domainSeparator',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(24, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * permit
	 *
	 * @param { ArgumentTypes.AccountId } owner,
	 * @param { ArgumentTypes.AccountId } spender,
	 * @param { (string | number | BN) } value,
	 * @param { (number | string | BN) } deadline,
	 * @param { ArgumentTypes.Signature } signature,
	 * @returns { Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError> }
	 */
	permit(
		owner: ArgumentTypes.AccountId,
		spender: ArgumentTypes.AccountId,
		value: string | number | BN,
		deadline: number | string | BN,
		signature: ArgumentTypes.Signature,
		__options?: GasLimit,
	): Promise<
		QueryReturnType<Result<Result<null, ReturnTypes.PSP22Error>, ReturnTypes.LangError>>
	> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Permit::permit',
			[owner, spender, value, deadline, signature],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(12, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * tokenDecimals
	 *
	 * @returns { Result<number, ReturnTypes.LangError> }
	 */
	tokenDecimals(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<number, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenDecimals',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(27, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * contractType
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	contractType(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::contractType',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * tokenName
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	tokenName(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenName',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * tokenSymbol
	 *
	 * @returns { Result<string | null, ReturnTypes.LangError> }
	 */
	tokenSymbol(
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<string | null, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'psp22Metadata::tokenSymbol',
			[],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(28, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}

	/**
	 * nonces
	 *
	 * @param { ArgumentTypes.AccountId } account,
	 * @returns { Result<number, ReturnTypes.LangError> }
	 */
	nonces(
		account: ArgumentTypes.AccountId,
		__options?: GasLimit,
	): Promise<QueryReturnType<Result<number, ReturnTypes.LangError>>> {
		return queryOkJSON(
			this.__apiPromise,
			this.__nativeContract,
			this.__callerAddress,
			'nonces::nonces',
			[account],
			__options,
			(result) => {
				return handleReturnType(result, getTypeDescription(30, DATA_TYPE_DESCRIPTIONS));
			},
		);
	}
}
